"use client";

import Link from "next/link";
import { Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { AuthCard } from "@/components/auth/AuthCard";
import { signUpWithEmail } from "@/lib/auth/browser";
import { safeAuthNext } from "@/lib/auth/redirect";

type OrganizationType =
  "business" | "nonprofit" | "government" | "education" | "other";

function SignupInner() {
  const router = useRouter();
  const params = useSearchParams();
  const next = safeAuthNext(params.get("next"));
  const [invite, setInvite] = useState(params.get("invite") ?? "");
  const [accountKind, setAccountKind] = useState<"personal" | "organization">(
    "personal",
  );
  const [organizationType, setOrganizationType] =
    useState<OrganizationType>("business");
  const [confirmationSent, setConfirmationSent] = useState(false);
  const field =
    "w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none transition focus:border-fuchsia-400/50";

  return (
    <AuthCard
      title="Create your account"
      cta="Sign up"
      extraFields={
        <>
          <input
            required={process.env.NEXT_PUBLIC_APP_ENV !== "staging"}
            autoComplete="off"
            placeholder={
              process.env.NEXT_PUBLIC_APP_ENV === "staging"
                ? "Beta invitation (optional on staging)"
                : "Private beta invitation"
            }
            value={invite}
            onChange={(event) => setInvite(event.target.value)}
            className={field}
          />
          <select
            value={accountKind}
            onChange={(event) =>
              setAccountKind(event.target.value as "personal" | "organization")
            }
            className={field}
          >
            <option value="personal">Personal</option>
            <option value="organization">Organization</option>
          </select>
          {accountKind === "organization" && (
            <select
              value={organizationType}
              onChange={(event) =>
                setOrganizationType(event.target.value as OrganizationType)
              }
              className={field}
            >
              <option value="business">Business</option>
              <option value="nonprofit">Nonprofit / NGO</option>
              <option value="government">Government</option>
              <option value="education">Education</option>
              <option value="other">Other</option>
            </select>
          )}
        </>
      }
      onSubmit={async (email, password) => {
        const { needsEmailConfirmation } = await signUpWithEmail({
          email,
          password,
          invite,
          accountKind,
          organizationType:
            accountKind === "organization" ? organizationType : undefined,
          next,
        });
        if (needsEmailConfirmation) setConfirmationSent(true);
        else router.push(next);
      }}
      done={
        confirmationSent ? (
          <>
            <h1 className="display text-2xl">Check your inbox</h1>
            <p className="mt-3 text-[var(--muted)]">
              We sent you a confirmation link. Click it to activate your
              account.
            </p>
          </>
        ) : null
      }
      footer={
        <>
          Already have an account?{" "}
          <Link
            href={`/login?next=${encodeURIComponent(next)}`}
            className="text-white underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    />
  );
}

export default function SignupPage() {
  return (
    <Suspense>
      <SignupInner />
    </Suspense>
  );
}
